import React from "react";
import { Box, Button, IconButton, Stack, TextField, Typography } from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { MentionEditor } from "./mention-editor";
import { PromptMentionSource } from "./mention-editor/types";
import { AssemblyReferenceTemplateColumnWrite } from "../types/assembly-template";
import { PromptDataType } from "../types/prompt-model";

interface TemplateColumnsEditorProps {
  columns: AssemblyReferenceTemplateColumnWrite[];
  onChange: (columns: AssemblyReferenceTemplateColumnWrite[]) => void;
  /**
   * The prompts a column template can reference via "#". Each column pulls a
   * field of any selected prompt (documents as rows, prompts as columns).
   */
  prompts?: PromptMentionSource[];
  /**
   * Old single-prompt experience: each column uses the legacy "@" field mention
   * against the reference's one selected prompt instead of the "#" cascade.
   */
  singlePrompt?: boolean;
  /**
   * The single prompt's data type (single-prompt mode). When ListOfValues, the
   * legacy "@" picker's Answer option drills into the List As list options.
   */
  anchorPromptDataType?: PromptDataType;
}

/**
 * Editor for the per-column templates used when a "List As" reference renders
 * as a table. Each column has a name and its own rich-text mention template.
 * The supplied {@link TemplateColumnsEditorProps.prompts} are the prompts a
 * column can reference via "#" — the table builder (documents as rows, prompts
 * as columns).
 */
const TemplateColumnsEditor = ({
  columns,
  onChange,
  prompts,
  singlePrompt = false,
  anchorPromptDataType,
}: TemplateColumnsEditorProps) => {
  const patch = (index: number, change: Partial<AssemblyReferenceTemplateColumnWrite>) => {
    onChange(columns.map((col, i) => (i === index ? { ...col, ...change } : col)));
  };

  return (
    <Stack spacing={1.5}>
      {columns.map((column, index) => (
        <Box
          key={index}
          sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1.5 }}
        >
          <Stack direction="row" spacing={1} alignItems="center" mb={1}>
            <TextField
              fullWidth
              size="small"
              label={`Column ${index + 1} name`}
              value={column.name}
              onChange={(e) => patch(index, { name: e.target.value })}
            />
            <IconButton
              size="small"
              color="error"
              onClick={() => onChange(columns.filter((_, i) => i !== index))}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Stack>
          <Typography variant="caption" color="textSecondary" sx={{ display: "block", mb: 0.5 }}>
            Column template
          </Typography>
          <MentionEditor
            value={column.template}
            onChange={(json) => patch(index, { template: json })}
            placeholder={
              singlePrompt
                ? "Type @ to insert the answer, quote or document name…"
                : "Type @ to insert a prompt, then . to pick its answer, document or quote…"
            }
            prompts={singlePrompt ? undefined : prompts}
            singlePrompt={singlePrompt}
            anchorPromptDataType={anchorPromptDataType}
          />
        </Box>
      ))}
      <Button
        startIcon={<Add />}
        variant="outlined"
        size="small"
        onClick={() => onChange([...columns, { name: "", template: { type: "doc", content: [] } }])}
      >
        Add column
      </Button>
    </Stack>
  );
};

export default TemplateColumnsEditor;
